import { CLIENT_STATS_STALE_AFTER_MS, type ClientStreamStatsMessage, type StreamHealthEvent } from '@playcastdotio/PeerCore';

type Dependencies = {
  nowMs: () => number;
  stale: (peerId: string) => void;
  emit: (peerId: string, event: StreamHealthEvent) => void;
  debug: (tag: string, detail: Record<string, unknown>) => void;
};

/** Receipt and sequence diagnostics; receipt alone grants no playback authority. */
export class RecoveryClientEvidence {
  private readonly seenAt = new Map<string, number>();
  private readonly stalePeers = new Set<string>();
  private readonly sequences = new Map<string, number>();
  constructor(private readonly deps: Dependencies) {}

  public observe(peerId: string, message: ClientStreamStatsMessage): void {
    this.check(peerId);
    this.seenAt.set(peerId, this.deps.nowMs());
    this.stalePeers.delete(peerId);
    const sequence = message.seq;
    if (sequence === undefined || !Number.isFinite(sequence)) return;
    const expected = this.sequences.get(peerId) ?? 0;
    if (expected > 0 && sequence > expected + 1) {
      this.deps.emit(peerId, { kind: 'client-stats-gap', stream: 'video', atMs: this.deps.nowMs(), detail: { from: expected, to: sequence } });
      this.deps.debug('[Recovery.clientStats.gap]', { peerId, from: expected, to: sequence });
    } else if (expected > 0 && sequence <= expected) {
      this.deps.debug('[Recovery.clientStats.seqReset]', { peerId, from: expected, to: sequence });
    }
    this.sequences.set(peerId, sequence);
  }

  public check(peerId: string): void {
    const lastSeenMs = this.seenAt.get(peerId);
    if (lastSeenMs === undefined || this.stalePeers.has(peerId)) return;
    const staleForMs = this.deps.nowMs() - lastSeenMs;
    if (Number.isFinite(staleForMs) && staleForMs >= 0 && staleForMs <= CLIENT_STATS_STALE_AFTER_MS) return;
    this.stalePeers.add(peerId);
    this.deps.emit(peerId, { kind: 'client-stats-stale', stream: 'video', atMs: this.deps.nowMs(), detail: { lastSeenMs, staleForMs } });
    this.deps.debug('[Recovery.clientStats.stale]', { peerId, lastSeenMs, staleForMs });
    this.deps.stale(peerId);
  }

  public isStale(peerId: string): boolean { return this.stalePeers.has(peerId); }
  public recentlyMeasured(peerId: string, windowMs: number): boolean {
    const at = this.seenAt.get(peerId);
    const age = at === undefined ? Infinity : this.deps.nowMs() - at;
    return age >= 0 && age < windowMs;
  }
  public clearPeer(peerId: string): void {
    this.seenAt.delete(peerId); this.stalePeers.delete(peerId); this.sequences.delete(peerId);
  }
  public clearAll(): void { this.seenAt.clear(); this.stalePeers.clear(); this.sequences.clear(); }
}
